import {
  CREDIT_SCORE_MODEL_RULE_FIELDS,
  CUSTOM_PARAMETER_TYPES_CONFIG,
  PERuleCategories,
  PERuleFields,
  PERuleFieldsCountyFipsCode,
  PERuleFieldsChannel,
  PERuleGroupBaseRate,
  PERuleGroupProperty,
  EntityTypes,
  STATE_FIPS,
  COMPLIANCE_RESULT_VALUE,
} from '@shared/constants';
import { CREDIT_SCORE_MODELS_RULES } from '@shared/constants/flags';
import {
  getRule,
  getEntities,
  getAudiences,
} from '@pe/services/configurations.js';
import { getAllCounties } from '@pe/services/geo.js';
import { onErrorHandler } from '@shared/utils/errorHandlers.js';
import {
  parseBooleanStructure,
  validResultStructure,
} from '@pe/components/Configure/RuleDetails/RulesLogic.js';
import clone from 'lodash/cloneDeep';
import api from '@shared/services/api.js';

import store from '@src/store';
import { RuleFields, CATEGORY_TO_FIELD_GROUP } from '@pe/services/ruleFields';

export { RuleFields, CATEGORY_TO_FIELD_GROUP };

export const isClampCategory = category => category === PERuleCategories.Clamp;

export async function constructRuleStatement(configId, ruleId, category) {
  try {
    const ruleFields = await getPERuleFields(configId);
    const ruleObject = await getRule(configId, ruleId);
    const clauses = await parseBooleanStructure(
      ruleObject.booleanEquationStructure,
      ruleFields,
    );
    if (ruleObject.resultEquationStructure) {
      validResultStructure(category, ruleObject.resultEquationStructure);
    }
    return { ruleFields, ruleObject, clauses };
  } catch (e) {
    onErrorHandler(e, 'construct-rule-statement', [403, 404]);
  }
}

export async function getPERuleFields(
  configurationId,
  params = null,
  audiences = null,
) {
  const disableFetchEntities = !!store?.state?.core?.isMiRateCard;

  if (!params) {
    params = disableFetchEntities
      ? []
      : await getEntities(configurationId, 'custom-parameters');
  }
  if (!audiences) {
    audiences = disableFetchEntities ? [] : await getAudiences(configurationId);
  }

  const ruleFields = {
    ...clone(PERuleFields),
  };
  params.forEach(value => {
    ruleFields[CATEGORY_TO_FIELD_GROUP[value.valueCategory]][value.name] = value
      .allowedValues?.length
      ? valueToObject(value)
      : CUSTOM_PARAMETER_TYPES_CONFIG[value.valueType].type;
  });

  const audiencesResult = audiences.map(audience => {
    return {
      id: audience.id,
      text: audience.name,
    };
  });

  const fields = {
    ...ruleFields,
    [PERuleGroupBaseRate]: {
      ...ruleFields[PERuleGroupBaseRate],
      [PERuleFieldsChannel]: audiencesResult,
    },
  };
  const isFlagActive = store?.getters?.['core/isFlagActive'];
  const creditModelRulesActive = !!isFlagActive?.(CREDIT_SCORE_MODELS_RULES);
  const hiddenFields = creditModelRulesActive
    ? []
    : CREDIT_SCORE_MODEL_RULE_FIELDS;
  const ruleFieldsObj = new RuleFields(
    fields,
    params,
    hiddenFields,
    creditModelRulesActive,
  );

  const [counties, valueGroups] = await Promise.all([
    getAllCounties(),
    configurationId && !disableFetchEntities
      ? getEntities(configurationId, EntityTypes.ValueGroup + 's')
      : Promise.resolve([]),
  ]);

  addCountyFipsCodeValues(ruleFieldsObj, counties);

  ruleFieldsObj.counties = counties;
  ruleFieldsObj.valueGroups = valueGroups;

  return ruleFieldsObj;
}

function addCountyFipsCodeValues(ruleFieldsObj, counties) {
  // adding possible values to the property.countyFips parameter
  ruleFieldsObj.fields[PERuleGroupProperty][PERuleFieldsCountyFipsCode] =
    counties
      .map(state =>
        state.counties.map(
          county => STATE_FIPS[state.state_code] + county.county_fips,
        ),
      )
      .flat()
      .sort()
      .map(countyFips => ({
        id: countyFips,
        text: countyFips,
      }));
}

export function valueToObject(value) {
  return value.allowedValues.map(allowedValue => {
    return {
      id: allowedValue,
      text: allowedValue,
    };
  });
}

export function convertLLPAforUI(minStructure, maxStructure) {
  /*https://pollyex.atlassian.net/browse/OC-212
    In all cases where Total LLPA is concerned - it should be creating a rule that says “MIN LLPA >= {UI value * -1}”
    which will give you your desired effect in every case that I can think of.
    If the user enters a negative number, I think you’d want to just MAX instead of MIN and flip the value.
    For example….
    User enters Max LLPA of 0 the rule that is sent to the PE should be “MIN LLPA 0”
    User enters Max LLPA of 3 the rule that is sent to PE should be “MIN LLPA -3”
    User enters Max LLPA of -1.5 the rule that is sent to the PE should be “MAX LLPA 1.5”*/
  if (minStructure[0]?.value) {
    maxStructure.splice(0, maxStructure.length);
    maxStructure.push(...minStructure);
    maxStructure[0].value = +maxStructure[0].value * -1;
    maxStructure[0].content = +maxStructure[0].content * -1;
    minStructure.splice(0, minStructure.length);
  } else if (maxStructure[0]?.value && +maxStructure[0].value > 0) {
    maxStructure[0].value = +maxStructure[0].value * -1;
    maxStructure[0].content = +maxStructure[0].content * -1;
  }
}

export function convertLLPAforSave(minStructure, maxStructure) {
  const notValid = [null, undefined, '', []];
  if (
    !notValid.includes(maxStructure[0]?.value) &&
    +maxStructure[0].value >= 0
  ) {
    minStructure.splice(0, minStructure.length);
    minStructure.push(...maxStructure);
    minStructure[0].value = +minStructure[0].value * -1;
    minStructure[0].content = +minStructure[0].content * -1;
    maxStructure.splice(0, maxStructure.length);
  } else if (
    !notValid.includes(maxStructure[0]?.value) &&
    +maxStructure[0].value < 0
  ) {
    maxStructure[0].value = +maxStructure[0].value * -1;
    maxStructure[0].content = +maxStructure[0].content * -1;
  }
}

export async function uploadGrid(file, gridHeaders, gridInfo, signal) {
  const fileName = file.name;
  const formData = new FormData();
  formData.append('grid', file, fileName);
  formData.append(
    'gridHeaders',
    JSON.stringify({
      columnHeaders: gridHeaders.headerXNoName,
      rowHeaders: gridHeaders.headerYNoName,
    }),
  );
  formData.append('gridInfo', JSON.stringify(gridInfo));
  return await api.postFormData('/pe/api/grid-upload', formData, { signal });
}

export function getComplianceResult(
  productLevelComplianceResults = [],
  rateLevelComplianceResults = [],
) {
  /**
   * Product level compliance failures will take precedence over rate level failures.
   * If Product level passes or is not present, check for rate level failures next.
   * If no failures, check for passes
   */
  if (failsCompliance(productLevelComplianceResults)) {
    return COMPLIANCE_RESULT_VALUE.FAIL;
  }
  if (failsCompliance(rateLevelComplianceResults)) {
    return COMPLIANCE_RESULT_VALUE.FAIL;
  }
  if (passesCompliance(rateLevelComplianceResults)) {
    return COMPLIANCE_RESULT_VALUE.PASS;
  }
  if (passesCompliance(productLevelComplianceResults)) {
    return COMPLIANCE_RESULT_VALUE.PASS;
  }
  return null;
}

function checkComplianceResults(complianceResults, value) {
  return complianceResults?.some(
    r => r.booleanEquationValue && r.complianceResultEquationValue === value,
  );
}

function failsCompliance(complianceResults) {
  return checkComplianceResults(
    complianceResults,
    COMPLIANCE_RESULT_VALUE.FAIL,
  );
}

function passesCompliance(complianceResults) {
  return checkComplianceResults(
    complianceResults,
    COMPLIANCE_RESULT_VALUE.PASS,
  );
}
